import React, { useState } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button, Icon } from '@fourthwall/components'
import { ArrowShortDownIcon, DragIcon, PencilIcon, TrashIcon } from '@fourthwall/icons'

import type { SectionSetting } from '../../../types'
import * as S from './EditableSettingsItem.styled'
import { InlineSettingEditor } from './InlineSettingEditor'

type EditableSettingsItemProps = {
  setting: SectionSetting
  onEdit: (setting: SectionSetting) => void
  onDelete: (settingId: string) => void
}

const SettingContent = ({ setting }: { setting: SectionSetting }) => {
  switch (setting.type) {
    case 'header':
      return (
        <S.SettingInfo>
          <S.SettingType>{setting.type}</S.SettingType>
          <S.SettingName>{setting.content}</S.SettingName>
        </S.SettingInfo>
      )
    default:
      return (
        <S.SettingInfo>
          <S.SettingType>{setting.type}</S.SettingType>
          <S.SettingName>{setting.label}</S.SettingName>
          <S.SettingId>
            key: <b>{setting.id}</b>
          </S.SettingId>
        </S.SettingInfo>
      )
  }
}

export const EditableSettingsItem = ({ setting, onEdit, onDelete }: EditableSettingsItemProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: setting.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleClose = () => {
    setIsEditing(false)
  }

  const handleSave = (updatedSetting: SectionSetting) => {
    onEdit(updatedSetting)
    setIsEditing(false)
  }

  const handleDelete = () => {
    onDelete(setting.id)
  }

  if (isEditing) {
    return (
      <InlineSettingEditor
        icon={<Icon component={PencilIcon} height={12} />}
        header={<SettingContent setting={setting} />}
        setting={setting}
        onSave={handleSave}
        onClose={handleClose}
      />
    )
  }

  return (
    <S.SettingItem ref={setNodeRef} style={style} {...attributes}>
      <S.SettingItemHeader>
        <S.DragHandle {...listeners}>
          <Icon component={DragIcon} height={12} />
        </S.DragHandle>
        <SettingContent setting={setting} />
        <S.SettingActions>
          <Button
            appearance="destructive-inverted"
            size="small"
            leftIcon={<TrashIcon height={12} />}
            onClick={handleDelete}
          />
          <Button
            appearance="semi-transparent"
            size="small"
            leftIcon={<ArrowShortDownIcon height={12} />}
            onClick={handleEdit}
          />
        </S.SettingActions>
      </S.SettingItemHeader>
    </S.SettingItem>
  )
}
